import { useEffect, useState, useCallback } from 'react';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';

export interface TasteProfile {
  userId: string;
  content: string;
  enabled: boolean;
  updatedAt?: string;
}

/**
 * Module-level singleton — the profile is read on every LLM call site, so
 * cache it once and let subscribers re-render when it changes.
 */
let _profile: TasteProfile | null = null;
const _listeners = new Set<() => void>();
function notify(): void { _listeners.forEach(fn => fn()); }

async function fetchProfile(): Promise<TasteProfile | null> {
  try {
    const res = await fetchWithCsrf('/api/taste-profile');
    if (!res.ok) return null;
    return await res.json() as TasteProfile;
  } catch {
    return null;
  }
}

/**
 * Format the profile as a prompt prefix block. Mirrors the server's
 * buildTasteProfileBlock so client-side and server-side LLM calls
 * inject the same header text.
 */
export function buildTasteProfileBlock(profile: TasteProfile | null): string {
  if (!profile || !profile.enabled || !profile.content.trim()) return '';
  return `PERSONAL TASTE PROFILE (preserve these preferences — do not contradict, do not echo back as lyrics):
${profile.content.trim()}

---

`;
}

/** Sync read for non-React callers (LLM service files). Returns the cached profile or null. */
export function getTasteProfileSync(): TasteProfile | null {
  return _profile;
}

/** Convenience: get the prepend block synchronously from the cached profile. */
export function getTasteProfileBlockSync(): string {
  return buildTasteProfileBlock(_profile);
}

export function useTasteProfile() {
  const [profile, setProfile] = useState<TasteProfile | null>(_profile);
  const [loading, setLoading] = useState(_profile === null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to module-level changes (so save in one place updates everywhere)
  useEffect(() => {
    const fn = () => setProfile(_profile);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  // Fetch once on first mount if not already loaded
  useEffect(() => {
    if (_profile !== null) return;
    let cancelled = false;
    (async () => {
      const p = await fetchProfile();
      if (cancelled) return;
      _profile = p ?? { userId: '', content: '', enabled: true };
      notify();
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (content: string, enabled: boolean): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/taste-profile', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, enabled }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `HTTP ${res.status}`);
      }
      const saved = await res.json() as TasteProfile;
      _profile = saved;
      notify();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return { profile, loading, saving, error, save };
}
